/**
 * Recurring invoices: the templates that bill a plan on a schedule.
 *
 * The port of `Views/Controls/RecurringInvoicesSection.xaml` + `RecurringInvoicesSectionViewModel`.
 *
 * THE CATALOG PICK IS A REFERENCE, NOT A RULE. Picking an item prefills the description and what
 * THIS customer pays - contract price, list price, or cost plus their markup - and says which rule
 * set it. Everything filled in stays editable, and the price stored on the template is what bills:
 * a plan priced from the catalog in March keeps billing what was agreed when the list price moves in
 * June. Reopening a template shows the pick WITHOUT re-quoting, because re-quoting on open would
 * silently move an agreed price to today's.
 *
 * Unlike a one-off charge, a markup-derived price is written in rather than left blank - there is no
 * "work it out at invoice time" for a line that has no cost of its own on the invoice.
 */
import type { Control, StackPanel } from 'webwin';
import * as crm from '../../api/crm.js';
import type {
  CatalogItemDto,
  RecurringInvoiceDto,
  RecurringInvoiceLineDto
} from '../../contracts.gen.js';
import { amount, money, plural } from '../../format.js';
import { isoDay, isoDayOf, middayUtcFromIso, todayIso } from '../../recurrence.js';
import { Card } from '../../ui/Card.js';
import { CardList } from '../../ui/CardList.js';
import { chip } from '../../ui/Chip.js';
import { dateBox } from '../../ui/DateBox.js';
import { confirm } from '../../ui/dialogs.js';
import { EmptyState } from '../../ui/EmptyState.js';
import { moneyBox, type MoneyBox } from '../../ui/MoneyBox.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { button, buttonRow, card, checkBox, comboBox, field, fieldRow, fillWith, FONT, GAP, h2, hstack, inlineRow, muted, relayout, splitRow, stackOf, textBox, value, vstack } from '../../ui/layout.js';
import {
  endsClause,
  failure,
  INTERVAL_NAMES,
  INTERVALS,
  loadCustomerChoices,
  readSchedule,
  type CustomerChoices,
  type RecurringSection,
  type RecurringSectionOptions
} from './recurring.js';

/** The first entry of every line's picker: a line typed by hand, off the price list. */
const CUSTOM_LINE = 'Custom line';

interface LineRow {
  readonly panel: StackPanel;
  readonly description: ReturnType<typeof textBox>;
  readonly quantity: ReturnType<typeof textBox>;
  readonly price: MoneyBox;
  catalogItemId: number | null;
  read(): { quantity: number; unitPrice: number } | null;
}

export function recurringInvoicesSection(options: RecurringSectionOptions): RecurringSection {
  const { ctx, lockCustomer, onChanged } = options;
  let customerFilterId = options.customerFilterId;

  let choices: CustomerChoices = { names: [], ids: [] };
  let catalog: CatalogItemDto[] = [];
  let editingId = 0;
  let busy = false;
  const lines: LineRow[] = [];

  const status = new StatusLine();

  // ---- header ----
  const runButton = button('Raise due invoices now', () => void run());
  const newButton = button('New recurring invoice', () => beginNew(), true);
  // Title, sentence, then the buttons on a wrapping row of their own. Beside the buttons the sentence
  // was clipped - Webwin measures a filling child's height at the row's full width - and the two
  // buttons do not fit beside a title on a phone anyway.
  const header = stackOf(
    vstack({ spacing: GAP.sm }),
    h2('Recurring invoices'),
    muted('Anything billed on a schedule - a managed-services plan, a hosted mailbox. Each period raises its own invoice.'),
    stackOf(buttonRow(), newButton, runButton)
  );

  // ---- editor ----
  const editorTitle = h2('New recurring invoice');
  const nameBox = textBox();
  const customerPicker = comboBox<string>([]);
  const intervalPicker = comboBox<string>([...INTERVAL_NAMES], {
    selectedIndex: 0,
    onSelectionChanged: () => updatePreview()
  });
  const dayBox = textBox({ text: '1' });
  const dueDaysBox = textBox({ text: '30' });
  const start = dateBox({ iso: todayIso(), onChange: () => updatePreview() });
  const end = dateBox({ onChange: () => updatePreview() });
  const activeCheck = checkBox('Active', true);
  const issueCheck = checkBox('Issue automatically');
  const emailCheck = checkBox('Email to the billing contact');
  const notesBox = textBox();
  const preview = muted('', 'subtle');
  const subtotalLabel = value('Subtotal $0.00 per invoice', { font: FONT.bold });

  dayBox.on('textchanged', () => updatePreview());
  dueDaysBox.on('textchanged', () => updatePreview());

  // Emailing issues, so the two ticks cannot disagree: unticking Issue unticks Email, and ticking
  // Email ticks Issue. The server normalizes it too; this is so the form never shows a state the
  // server would silently change.
  issueCheck.on('change', () => {
    if (!issueCheck.checked && emailCheck.checked) emailCheck.checked = false;
  });
  emailCheck.on('change', () => {
    if (emailCheck.checked && !issueCheck.checked) issueCheck.checked = true;
  });

  const lineHost = vstack({ spacing: GAP.sm });
  const customerField = field('Customer', customerPicker);
  customerField.visible = !lockCustomer;

  const editor: Card = card({ visible: false });
  editor.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      editorTitle,
      fieldRow(field('Name (the heading on every invoice it raises)', nameBox), customerField),
      fieldRow(
        field('Repeats', intervalPicker),
        field('Day of the month', dayBox),
        field('Payable within (days)', dueDaysBox)
      ),
      fieldRow(field('Starting', start.picker), field('Ending (optional)', end.picker)),
      preview,
      lineHost,
      stackOf(
        hstack({ spacing: GAP.md }),
        button('Add line', () => addLine()),
        subtotalLabel,
        muted("Tax is added at the customer's rate when each invoice is raised.")
      ),
      stackOf(hstack({ spacing: GAP.lg }), activeCheck, issueCheck, emailCheck),
      muted(
        'Left unticked, each invoice lands as a draft to review and issue by hand. Issue automatically makes it payable the moment it is raised; emailing it sends the PDF through the instance mail account and implies issuing. A send that fails leaves the invoice issued, with the failure on its email history.'
      ),
      field('Notes', notesBox),
      stackOf(
        buttonRow(),
        button('Save', () => void save(), true),
        button('Cancel', () => closeEditor())
      )
    )
  );

  // ---- list ----
  const list = new CardList<RecurringInvoiceDto>({
    key: t => t.id,
    build: t => buildRow(t)
  });
  const empty = new EmptyState(
    'No recurring invoices yet. Add one for anything paid for on a schedule, and the invoice is raised for you on the day.'
  );

  const panel = stackOf(
    vstack({ spacing: GAP.md }),
    header,
    status.label,
    editor,
    empty.panel,
    list.panel
  );

  // -----------------------------------------------------------------
  //  Rows
  // -----------------------------------------------------------------

  function buildRow(t: RecurringInvoiceDto): Card {
    const heading = inlineRow();
    heading.controls.add(value(t.name, { font: FONT.bold }));
    if (!lockCustomer) heading.controls.add(value(t.customerName, { font: FONT.small, role: 'muted' }));
    if (!t.active) heading.controls.add(chip('paused', 'warn'));
    if (t.autoIssue) heading.controls.add(chip('issues automatically', 'info'));
    if (t.autoEmail) heading.controls.add(chip('emails', 'info'));

    const detail = [
      `${t.intervalLabel} on day ${t.dayOfMonth}`,
      t.nextDueUtc ? `next ${isoDayOf(t.nextDueUtc)}` : 'nothing further due',
      `${t.generatedCount} raised so far`
    ].join('  ·  ');

    const left = stackOf(vstack({ spacing: GAP.xs }), heading, muted(detail));

    const actions = hstack({ spacing: GAP.sm });
    actions.controls.add(value(money(t.subtotal), { font: FONT.bold }));
    actions.controls.add(button('Edit', () => beginEdit(t)));
    actions.controls.add(button('Remove', () => void remove(t)));

    const row = splitRow();
    fillWith(row, left);
    row.controls.add(actions);

    const shell = card({ padding: { left: 12, top: 12, right: 12, bottom: 12 } });
    shell.controls.add(row);
    return shell;
  }

  // -----------------------------------------------------------------
  //  Line rows
  // -----------------------------------------------------------------

  function catalogNames(): string[] {
    return [CUSTOM_LINE, ...catalog.map(i => (i.sku ? `${i.sku} - ${i.name}` : i.name))];
  }

  function addLine(
    description = '',
    quantity = '1',
    unitPrice = '',
    catalogItemId: number | null = null
  ): LineRow {
    const descriptionBox = textBox({ text: description });
    const quantityBox = textBox({ text: quantity, width: 70, hAlign: 'start' });
    const price = moneyBox({ width: 110, hAlign: 'start' });
    if (unitPrice) price.box.text = unitPrice;

    const explanation = muted('', 'subtle');
    explanation.visible = false;

    const picker = comboBox<string>(catalogNames(), {
      selectedIndex: catalogItemId === null ? 0 : Math.max(0, catalog.findIndex(i => i.id === catalogItemId) + 1)
    });

    const row: LineRow = {
      panel: vstack({ spacing: GAP.xs }),
      description: descriptionBox,
      quantity: quantityBox,
      price,
      catalogItemId,
      read: () => {
        const qty = Number.parseFloat(quantityBox.text.replace(/,/g, ''));
        const each = price.value();
        if (!Number.isFinite(qty) || each === null) return null;
        return { quantity: qty, unitPrice: each };
      }
    };

    // The pick is applied only when the person changes it - never when the row is built from a
    // stored template, which is what keeps an agreed price from drifting to today's list price.
    picker.on('selectionchanged', () => void applyCatalog(row, picker.selectedIndex, explanation));

    quantityBox.on('textchanged', () => updateSubtotal());
    price.box.on('textchanged', () => updateSubtotal());

    const fields = splitRow({ spacing: GAP.sm, align: 'end' });
    fillWith(fields, field('From the price list', picker), 2);
    fillWith(fields, field('Description', descriptionBox), 3);
    fields.controls.add(field('Qty', quantityBox));
    fields.controls.add(field('Unit price', price.row));
    fields.controls.add(button('Remove', () => removeLine(row)));

    row.panel.controls.add(fields);
    row.panel.controls.add(explanation);

    lines.push(row);
    lineHost.controls.add(row.panel);
    updateSubtotal();
    return row;
  }

  function removeLine(row: LineRow): void {
    const at = lines.indexOf(row);
    if (at >= 0) lines.splice(at, 1);
    lineHost.controls.remove(row.panel);
    // A template with no lines cannot be saved, so an empty editor gets one back rather than
    // presenting a form whose Save is guaranteed to be refused.
    if (lines.length === 0) addLine();
    updateSubtotal();
    relayout(editor);
  }

  function clearLines(): void {
    lines.length = 0;
    lineHost.controls.clear();
  }

  async function applyCatalog(row: LineRow, index: number, explanation: Control & { text: string; visible: boolean }): Promise<void> {
    const item = index > 0 ? catalog[index - 1] : undefined;
    if (!item) {
      row.catalogItemId = null;
      explanation.text = '';
      explanation.visible = false;
      relayout(editor);
      return;
    }

    row.catalogItemId = item.id;
    row.description.text = item.sku ? `${item.sku} - ${item.name}` : item.name;

    const customerId = selectedCustomerId();
    if (customerId !== null) {
      try {
        const quote = await crm.quoteCatalogItem(customerId, item.id, ctx.dispose.signal());
        if (ctx.cancelled()) return;
        row.price.set(quote.unitPrice);
        explanation.text = `${money(quote.unitPrice)} - ${quote.explanation} Edit it if this plan bills differently.`;
        explanation.visible = true;
        updateSubtotal();
        relayout(editor);
        return;
      } catch {
        // Fall through to the item's own figures: a quote the server would not give is not a reason
        // to leave the line blank.
      }
    }

    const fallback = item.listPrice ?? item.unitCost;
    row.price.set(fallback);
    explanation.text =
      item.listPrice === null
        ? `${money(fallback)} - the item's cost; no customer quote was available. Edit it as needed.`
        : `${money(fallback)} - list price. Edit it if this plan bills differently.`;
    explanation.visible = true;
    updateSubtotal();
    relayout(editor);
  }

  function updateSubtotal(): void {
    let total = 0;
    for (const row of lines) {
      const read = row.read();
      if (read) total += Math.round(read.quantity * read.unitPrice * 100) / 100;
    }
    subtotalLabel.text = `Subtotal $${amount(total)} per invoice`;
  }

  // -----------------------------------------------------------------
  //  Editor
  // -----------------------------------------------------------------

  function selectedCustomerId(): number | null {
    const index = customerPicker.selectedIndex;
    return index >= 0 && index < choices.ids.length ? choices.ids[index]! : null;
  }

  function preselectedCustomerIndex(): number {
    if (customerFilterId === undefined) return choices.ids.length > 0 ? 0 : -1;
    return Math.max(0, choices.ids.indexOf(customerFilterId));
  }

  function beginNew(): void {
    editingId = 0;
    editorTitle.text = 'New recurring invoice';
    nameBox.text = '';
    customerPicker.selectedIndex = preselectedCustomerIndex();
    intervalPicker.selectedIndex = 0;
    dayBox.text = '1';
    dueDaysBox.text = '30';
    start.set(todayIso());
    end.set(null);
    activeCheck.checked = true;
    issueCheck.checked = false;
    emailCheck.checked = false;
    notesBox.text = '';
    clearLines();
    addLine();
    openEditor();
  }

  function beginEdit(t: RecurringInvoiceDto): void {
    editingId = t.id;
    editorTitle.text = `Edit recurring invoice #${t.id}`;
    nameBox.text = t.name;
    customerPicker.selectedIndex = Math.max(0, choices.ids.indexOf(t.customerId));
    intervalPicker.selectedIndex = Math.max(0, INTERVALS.indexOf(t.interval));
    dayBox.text = String(t.dayOfMonth);
    dueDaysBox.text = String(t.dueDays);
    start.set(isoDayOf(t.startUtc));
    end.set(t.endUtc ? isoDayOf(t.endUtc) : null);
    activeCheck.checked = t.active;
    issueCheck.checked = t.autoIssue;
    emailCheck.checked = t.autoEmail;
    notesBox.text = t.notes;

    clearLines();
    for (const line of t.lines) {
      addLine(line.description, String(line.quantity), amount(line.unitPrice), line.catalogItemId);
    }
    if (lines.length === 0) addLine();
    openEditor();
  }

  function openEditor(): void {
    updatePreview();
    updateSubtotal();
    editor.visible = true;
    relayout(panel);
    nameBox.focus();
  }

  function closeEditor(): void {
    editor.visible = false;
    relayout(panel);
  }

  /** "First invoice on 2026-10-05, then monthly, due 30 days after each." */
  function updatePreview(): void {
    const reading = readSchedule(intervalPicker.selectedIndex, dayBox.text, start.iso(), end.iso());
    if (!reading.first) {
      preview.text = reading.problem;
      return;
    }
    const dueDays = Number.parseInt(dueDaysBox.text, 10);
    const days = Number.isFinite(dueDays) ? dueDays : 30;
    const cadence = INTERVAL_NAMES[intervalPicker.selectedIndex]?.toLowerCase() ?? '';

    let message = `First invoice on ${isoDay(reading.first)}, then ${cadence}, due ${days} days after each.`;
    if (reading.owed > 0) {
      message += ` Saving raises ${plural(reading.owed, 'invoice')} already owed since the start date.`;
    }
    message += endsClause(end.iso());
    preview.text = message;
  }

  async function save(): Promise<void> {
    if (busy) return;
    const name = nameBox.text.trim();
    if (!name) {
      status.error('A recurring invoice needs a name - it is the heading on every invoice it raises.');
      return;
    }
    const customerId = selectedCustomerId();
    if (customerId === null) {
      status.error('Pick the customer this invoice bills.');
      return;
    }
    const day = Number.parseInt(dayBox.text, 10);
    if (!Number.isFinite(day) || day < 1 || day > 31) {
      status.error('Day of the month must be between 1 and 31.');
      return;
    }
    const dueDays = Number.parseInt(dueDaysBox.text, 10);
    if (!Number.isFinite(dueDays) || dueDays < 0 || dueDays > 365) {
      status.error('Due days must be between 0 and 365.');
      return;
    }

    const body: RecurringInvoiceLineDto[] = [];
    for (const row of lines) {
      const description = row.description.text.trim();
      // A blank row is the one the editor always leaves at the bottom, not a mistake to refuse.
      if (!description && !row.price.text().trim()) continue;
      const read = row.read();
      if (!read || read.quantity <= 0 || read.unitPrice < 0) {
        status.error(`Check the quantity and unit price on "${description || 'the blank line'}".`);
        return;
      }
      body.push({
        description,
        notes: '',
        quantity: read.quantity,
        unitPrice: read.unitPrice,
        catalogItemId: row.catalogItemId,
        amount: Math.round(read.quantity * read.unitPrice * 100) / 100
      });
    }
    if (body.length === 0) {
      status.error('Add at least one line - what is being charged for.');
      return;
    }

    const startUtc = middayUtcFromIso(start.iso()) ?? middayUtcFromIso(todayIso())!;
    const endUtc = middayUtcFromIso(end.iso());

    busy = true;
    status.busy('Saving…');
    try {
      const request = {
        customerId,
        name,
        interval: INTERVALS[Math.max(0, intervalPicker.selectedIndex)]!,
        dayOfMonth: day,
        startUtc: startUtc.toISOString(),
        endUtc: endUtc ? endUtc.toISOString() : null,
        active: activeCheck.checked,
        lines: body,
        dueDays,
        autoIssue: issueCheck.checked,
        autoEmail: emailCheck.checked,
        notes: notesBox.text.trim()
      };
      if (editingId === 0) await crm.createRecurringInvoice(request);
      else await crm.updateRecurringInvoice(editingId, request);
      if (ctx.cancelled()) return;
      status.ok(`Saved ${name}. Any invoices it already owed have been raised.`);
      closeEditor();
      await refresh();
      onChanged?.();
    } catch (error) {
      if (!ctx.cancelled()) status.error(failure('Could not save the recurring invoice', error));
    } finally {
      busy = false;
    }
  }

  async function remove(t: RecurringInvoiceDto): Promise<void> {
    const ok = await confirm(ctx.desktop, {
      title: 'Remove recurring invoice',
      message:
        `Stop "${t.name}" for ${t.customerName}? ` +
        `The ${plural(t.generatedCount, 'invoice')} it already raised are kept.`
    });
    if (!ok || ctx.cancelled()) return;

    status.busy('Removing…');
    try {
      await crm.deleteRecurringInvoice(t.id);
      if (ctx.cancelled()) return;
      status.ok(`Removed the recurring invoice ${t.name}. Invoices already raised are unchanged.`);
      if (editingId === t.id) closeEditor();
      await refresh();
      onChanged?.();
    } catch (error) {
      if (!ctx.cancelled()) status.error(failure('Could not remove that recurring invoice', error));
    }
  }

  async function run(): Promise<void> {
    if (busy) return;
    busy = true;
    status.busy('Raising due invoices…');
    try {
      const result = await crm.runRecurringInvoices();
      if (ctx.cancelled()) return;
      let message =
        result.created === 0
          ? `Nothing due - all ${plural(result.templatesConsidered, 'recurring invoice')} are up to date.`
          : `Raised ${plural(result.created, 'invoice')} from ${plural(
              result.templatesConsidered,
              'template'
            )}.`;
      // A failed send leaves the invoice issued with the failure on its own email history, so the
      // count is worth reporting here rather than leaving it to be discovered one invoice at a time.
      if (result.emailed > 0) message += ` ${result.emailed} emailed.`;
      if (result.emailFailed > 0) {
        message += ` ${result.emailFailed} could not be emailed - each is still issued, with the failure on its email history.`;
      }
      status.ok(message);
      await refresh();
      onChanged?.();
    } catch (error) {
      if (!ctx.cancelled()) status.error(failure('Could not raise the recurring invoices', error));
    } finally {
      busy = false;
    }
  }

  // -----------------------------------------------------------------
  //  Loading
  // -----------------------------------------------------------------

  async function refresh(): Promise<void> {
    try {
      const all = await crm.getRecurringInvoices(ctx.dispose.signal());
      if (ctx.cancelled()) return;
      const mine =
        customerFilterId === undefined ? all : all.filter(t => t.customerId === customerFilterId);
      list.set(mine);
      empty.update(mine.length);
      relayout(panel);
    } catch (error) {
      if (!ctx.cancelled()) status.error(failure('Could not load the recurring invoices', error));
    }
  }

  async function initialize(): Promise<void> {
    choices = await loadCustomerChoices(ctx.dispose.signal());
    if (ctx.cancelled()) return;
    customerPicker.items = choices.names;
    customerPicker.selectedIndex = preselectedCustomerIndex();

    try {
      catalog = await crm.getCatalog(false, ctx.dispose.signal());
    } catch {
      // Custom lines still work; only the picker needs the price list.
      catalog = [];
    }
    if (ctx.cancelled()) return;

    await refresh();
  }

  /** Narrows the list, and preselects the editor, to another customer. Never moves an open editor. */
  async function setCustomerFilter(id: number | undefined): Promise<void> {
    customerFilterId = id;
    await refresh();
  }

  return { panel, initialize, refresh, setCustomerFilter };
}
